"use client";
import {
  ForwardedRef,
  forwardRef,
  InputHTMLAttributes,
  memo,
  ReactNode,
  useId,
  useMemo,
} from "react";

import { Input } from "@nextui-org/react";

import FormWrapper from "@/ui/Forms/FormWrapper";
import clsx from "clsx";

export interface BaseInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "required" | "size"> {
  label?: string;
  error?: string;
  insideRightComponent?: ReactNode;
  insideLeftComponent?: ReactNode;
  labelRightComponent?: ReactNode;
  required?: boolean;
  errorClassName?: string;
  size?: "sm" | "md" | "lg";
  placeholder?: string;
}

const classNames = {
  base: "form-number-input-base",
  mainWrapper: "form-number-input-wrapper",
  inputWrapper: "form-number-input-container",
  input: clsx(
    "form-number-input",
    "disabled:!bg-transparent",
    "disabled:!opacity-100",
    "disabled:!text-black",
  ),
  inputWrapper: clsx(
    "form-number-input-container",
    "disabled:!bg-transparent",
    "disabled:!opacity-100",
  ),
  errorMessage: "form-number-input-error",
};

const BaseInputComponent = (
  {
    label,
    error,
    insideRightComponent,
    insideLeftComponent,
    labelRightComponent,
    required = false,
    className,
    errorClassName,
    placeholder,
    ...inputProps
  }: BaseInputProps,
  ref: ForwardedRef<HTMLInputElement>,
) => {
  const fallbackId = useId();

  const endContent = useMemo(() => {
    if (insideRightComponent) {
      return (
        <div className="form-input-end-content">{insideRightComponent}</div>
      );
    }
    return null;
  }, [insideRightComponent]);

  const startContent = useMemo(() => {
    if (insideLeftComponent) {
      return (
        <div className="form-input-start-content">{insideLeftComponent}</div>
      );
    }
    return null;
  }, [insideLeftComponent]);

  return (
    <FormWrapper
      label={label}
      error={error}
      required={required}
      labelRight={labelRightComponent}
      errorClassName={errorClassName}
    >
      {/* @ts-expect-error input value may be number but NextUI typing is narrow */}
      <Input
        ref={ref}
        isInvalid={!!error}
        startContent={startContent}
        endContent={endContent}
        placeholder={placeholder}
        classNames={classNames}
        className={clsx(
          className,
          "[&_input]:bg-clip-padding [&_input]:!shadow-none [&_input:-webkit-autofill]:!shadow-none [&_input:-webkit-autofill]:!border-none [&_input:-webkit-autofill]:![--tw-ring-shadow:0_0_#0000]",
        )}
        {...inputProps}
        id={inputProps.id ?? inputProps.name ?? fallbackId}
      />
    </FormWrapper>
  );
};

const BaseInput = memo(forwardRef(BaseInputComponent));

// Добавляем displayName для отладки
BaseInput.displayName = "BaseInput";

export default BaseInput;
